import { seeded, seg, tween } from '../../../core/effect/motion';
import {
  CARD_ASPECT, add, canvasOf, fade, fill, place, sprite,
} from './common';
import { LINK_URL } from './chainPlate';

/**
 * **無効にされた瞬間の見せ場**（Issue #591。指示書 `../direction/chain_negated.md`。キービジュアルは無い＝方向は人が決めた）。
 * `effects/chain_negated.js`（発動の無効）と `effects/chain_disabled.js`（効果の無効）はこれを呼ぶだけ。
 * **違うのは鎖の見た目だけ**（`BIND`。語彙は `./chainPlate.js::BINDS` と同じ: 発動の無効 = X 字の太い鎖 / 効果の無効 = イラストの側に横の細い鎖 2 本）。
 *
 * ```
 * 0      暗転（HUD の下の黒い板 0.4）。無効にされたカード（高さ 720）が鋼の縦長の板に載って、発動したプレイヤーの側から滑り込む
 * 160    急停止（行き過ぎて戻る）
 * 160    鎖が画面の外から加速してカードへ伸びる（negated: 左上 → 右下・右上 → 左下 / disabled: 左から・右から横に）
 * 420    ガチン！ 鎖が掛かって締まる: カードが 0.94 に縮む・カードの形の閃光 2 フレーム・鎖がカードの縁を越える点から硬い火花・
 *        色収差 1 フレーム → **ヒットストップ 100ms**（火花も止める）→ 揺れ（negated 32 / disabled 20）とカードが戻る
 * 520    無効化の印（丸に斜線の禁止の印。`kit.negateMarkTexture`）が上から大きく落ちてくる
 * 640    印の着地: 印の形の閃光 2 フレーム・縁から硬い火花・揺れ 10・印が少し潰れて戻る
 * 640    見得。カードの色が抜けていく（灰色の板を 0.7 まで。完全な灰色にはしない）
 * 1150   はける: 印は横に潰れ（〜1350）、鎖ごとカードと板は縦に潰れる（〜1450）。暗転が明ける。潰れきる瞬間に横の硬い線 1〜2 フレーム
 * 1450   何も残らない（〜1500）
 * ```
 *
 * **文字は出さない**（NEGATE / DISABLE の文字と斜めの帯はやめた。ユーザー 2026-09-28）。**札・カードは割れない**（破壊は move の担当）。
 * **盤は触らない**（カードは中央に大きく出すだけ。場に居るかは問わない）。もやもやした光は使わない。
 */
export const D = 1500;
export const SLIDE_END = 160;   // 滑り込んで急停止
const SETTLE = 140;             // 行き過ぎから戻る
export const CHAIN_IN = 160;    // 鎖が飛び始める
export const CLAMP = 420;       // 鎖が締まる
export const STOP = 100;        // ヒットストップ
export const MARK_IN = 520;     // 印が落ち始める
export const MARK_HIT = 640;    // 印の着地
export const EXIT = 1150;       // はけ始める
const MARK_GONE = 1350;         // 印が潰れきる
export const GONE = 1450;       // 全部消える

const CARD_H = 720;
const CARD_W = CARD_H * CARD_ASPECT;
const CY = 560;                 // カードの中心（上 200 / 下 920）
const PLATE_W = CARD_W + 120;
const PLATE_H = CARD_H + 120;
const OVER_PX = 36;             // 急停止の行き過ぎ（px）
const SQUEEZE = 0.94;           // 締まった瞬間の縮み
const DIM = 0.4;
const MARK = 540;               // 印の一辺
const MARK_FROM = 2.0;          // 落ち始めの倍率
const GREY = 0.7;               // 見得の終わりの灰色の濃さ
const FAR = 1500;               // 鎖の尾（画面の外）までの長さ
const PAST = 60;                // 鎖の頭がカードの縁を越える長さ
const SPARK_LIFE = 0.4;
const LINK_ASPECT = 300 / 1002; // chainLink.png

/** ヒットストップの時間の写し（締まった後の動きは STOP だけ遅れる） */
const hs = (t) => (t < CLAMP ? t : (t < CLAMP + STOP ? CLAMP : t - STOP));

const norm = ([x, y]) => { const l = Math.hypot(x, y) || 1; return [x / l, y / l]; };

/**
 * **鎖の掛け方**（カードの中心からの位置 `at`（カードの幅・高さに対する比）と向き `dir`、遅れ `delay`）。
 * `len` は鎖の画像 1 枚（輪 3 つ）の長さ（px）、`fat` は太らせる倍率、`shake` は締まったときの揺れ
 */
export const BIND = Object.freeze({
  negated: Object.freeze({
    lines: [
      { at: [0, 0], dir: norm([CARD_W * 1.08, CARD_H * 0.9]), delay: 0 },
      { at: [0, 0], dir: norm([-CARD_W * 1.08, CARD_H * 0.8]), delay: 40 },
    ],
    len: 210,
    fat: 1.5,
    shake: 32,
  }),
  disabled: Object.freeze({
    lines: [
      { at: [0, -0.15], dir: norm([1, 0.06]), delay: 0 },
      { at: [0, 0.08], dir: norm([-1, 0.06]), delay: 40 },
    ],
    len: 130,
    fat: 1.0,
    shake: 20,
  }),
});

/** 点 `p`（カードの中心が原点）から向き `d` で進んだときにカードの縁に着く距離 */
function edgeDist(p, d) {
  const hw = CARD_W / 2; const hh = CARD_H / 2;
  const tx = Math.abs(d[0]) > 1e-6 ? ((d[0] > 0 ? hw : -hw) - p[0]) / d[0] : Infinity;
  const ty = Math.abs(d[1]) > 1e-6 ? ((d[1] > 0 ? hh : -hh) - p[1]) / d[1] : Infinity;
  return Math.min(tx, ty);
}

/**
 * **鎖 1 本の線**（カードの中心が原点）: 尾（画面の外）・頭（カードの縁を越えたところ）・カードの縁を横切る 2 点（火花の出どころ）
 */
export function chainLine(line) {
  const p = [line.at[0] * CARD_W, line.at[1] * CARD_H];
  const d = line.dir;
  const fwd = edgeDist(p, d);
  const back = edgeDist(p, [-d[0], -d[1]]);
  return {
    d,
    tail: [p[0] - d[0] * FAR, p[1] - d[1] * FAR],
    head: [p[0] + d[0] * (fwd + PAST), p[1] + d[1] * (fwd + PAST)],
    hits: [[p[0] + d[0] * fwd, p[1] + d[1] * fwd], [p[0] - d[0] * back, p[1] - d[1] * back]],
  };
}

/** カードの中心の x（発動したプレイヤーの側から滑り込んで急停止 → 行き過ぎから戻る） */
export function cardX(t, W, side) {
  const cx = W / 2;
  const x0 = cx + side * (W / 2 + PLATE_W);
  if (t < SLIDE_END) return x0 + (cx - x0) * tween(t, 0, SLIDE_END, 'power3_in');
  return cx - side * OVER_PX * (1 - tween(t, SLIDE_END, SLIDE_END + SETTLE, 'back_out'));
}

/**
 * **無効の見せ場の spec**（`defineEffect` に渡す）。
 * @param {object} o
 * @param {string} o.event `chain_negated` / `chain_disabled`
 * @param {string} o.label
 * @param {string} o.color 帯の色
 * @param {'negated'|'disabled'} o.bind 鎖の見た目
 */
export function negateBank({ situation, label, color: bandColor, bind }) {
  const look = BIND[bind];
  return {
    situation,
    label,
    color: bandColor,
    /** `data` はチェーンの番号だけ。カードは port が chain_stack から引いた `payload.link` */
    opens: ({ payload }) => {
      const link = payload?.link;
      if (!link) return null;
      return {
        ...payload, bind, player: link.player === 1 ? 1 : 0, code: link.code ?? null, uid: link.uid ?? null,
      };
    },
    duration: () => D,
    scene: [
      'reset',
      {
        ms: 0,
        grade: {
          // 色収差は締まった 1 フレームだけ。止めの間はコントラストを上げて硬く
          chroma: { keys: [{ ms: CLAMP - 1, value: 0 }, { ms: CLAMP, value: 0.014 }, { ms: CLAMP + 34, value: 0, ease: 'ease_out' }] },
          contrast: { keys: [{ ms: CLAMP - 1, value: 1 }, { ms: CLAMP, value: 1.3 }, { ms: CLAMP + STOP, value: 1.3, ease: 'hold' }, { ms: CLAMP + STOP + 1, value: 1 }] },
        },
      },
      { ms: CLAMP + STOP, shake: { amp: look.shake, freq: 24, decayMs: 360 } },
      { ms: MARK_HIT, shake: { amp: 10, freq: 30, decayMs: 180 } },
    ],
    sounds: () => [
      { at: CLAMP - 15, name: 'revoked' },
      { at: MARK_HIT - 20, name: 'attack', gain: 0.6 },
    ],

    layers: [
      {
        id: 'bind',
        label: `暗転・鋼の板・無効にされたカード・鎖（${bind === 'negated' ? 'X 字の太い鎖' : '横の細い鎖 2 本'}）・無効化の印`,
        space: 'screen',
        script: (ctx) => {
          const { THREE, kit } = ctx;
          const { w: W, h: H } = canvasOf(ctx);
          const { payload } = ctx;
          const side = payload.player === 1 ? 1 : -1;
          const cx = W / 2;
          const root = new THREE.Group();
          /** 描く順を group で固定する（three の透明物は一番近い祖先の group の renderOrder で並ぶ） */
          const layer = (order) => add(root, new THREE.Group(), order);
          const dimG = layer(0);
          const trailG = layer(1);
          const bound = layer(2);
          const markG = layer(5);
          const sparkG = layer(6);

          // --- 暗転
          const dim = add(dimG, place(fill(ctx, '#050507', W, H), W / 2, H / 2), 0);

          // --- 滑り込みの硬い線（カードの後ろ。急停止で消える）
          const rand = seeded(`${ctx.seed}:negate`);
          const trails = Array.from({ length: 12 }, (_, i) => ({
            m: add(trailG, fill(ctx, i % 3 === 0 ? '#ffffff' : '#c9ccd4', 1, 1), 1),
            dy: (rand() - 0.5) * CARD_H * 0.9,
            len: 240 + rand() * 360,
            w: 2 + rand() * 4,
          }));

          // --- 鋼の板とカード（表の絵。無ければ持ち主のスリーブ）と、灰色の板・閃光
          add(bound, sprite(ctx, kit.steelPlateTexture(Math.round(PLATE_W), Math.round(PLATE_H), { cut: 48 }), PLATE_W, PLATE_H), 1);
          const img = (payload.code != null ? ctx.cards?.image?.(payload.code) : null) ?? ctx.cards?.sleeve?.(payload.player) ?? null;
          const tex = kit.imageTexture(img);
          const faceMat = kit.cardArtMaterial(tex, { brightness: 1.15, saturation: 1.3 });
          if (!tex) faceMat.color?.set?.('#28324e');
          const face = add(bound, kit.quad(faceMat, CARD_W, CARD_H), 2);
          const greyMat = tex ? kit.cardArtMaterial(tex, { brightness: 0.5, saturation: 0 }) : kit.plain(null, 1);
          if (!tex) greyMat.color?.set?.('#2a2a2a');
          const grey = add(bound, kit.quad(greyMat, CARD_W, CARD_H), 2.2);
          const flash = add(bound, fill(ctx, '#ffffff', CARD_W * 1.02, CARD_H * 1.02), 2.5);

          // --- 鎖（chainLink.png を線に沿って並べる。頭から後ろへ）
          const lh = look.len;
          const lw = lh * LINK_ASPECT * look.fat;
          const step = lh * 0.92;
          const linkTex = kit.urlTexture(LINK_URL);
          const chains = look.lines.map((line, ci) => {
            const geo = chainLine(line);
            const total = Math.hypot(geo.head[0] - geo.tail[0], geo.head[1] - geo.tail[1]);
            const n = Math.ceil(total / step) + 1;
            const links = Array.from({ length: n }, () => add(bound, sprite(ctx, linkTex, lw, lh), 3 + ci * 0.1));
            return { line, geo, total, links };
          });

          // --- 無効化の印（落ちてくる）と、着地の閃光（印の形）
          const markTex = kit.negateMarkTexture(512);
          const mark = add(markG, sprite(ctx, markTex, MARK, MARK), 1);
          const markFlash = add(markG, sprite(ctx, markTex, MARK, MARK, { blend: 'add' }), 2);

          // --- 硬い火花（締まった点から / 印の縁から）
          const sparkCols = ['#ffffff', '#ffd54a', '#ffb020', '#fff2b0'];
          const hits = chains.flatMap((c) => c.geo.hits.map((p) => ({ p, d: c.geo.d })));
          const clampSparks = [];
          hits.forEach((h, hi) => {
            for (let i = 0; i < 12; i += 1) {
              const m = add(sparkG, fill(ctx, sparkCols[(i + hi) % sparkCols.length], 1, 1), 1);
              // 鎖の向きに直交する向きを中心に、外へ散る
              const n = Math.atan2(h.d[1], h.d[0]) + (rand() < 0.5 ? Math.PI / 2 : -Math.PI / 2);
              clampSparks.push({
                m, p: h.p, a: n + (rand() - 0.5) * 1.6, len: 90 + rand() * 150, w: 3 + rand() * 3, speed: 700 + rand() * 1100,
              });
            }
          });
          const markSparks = Array.from({ length: 18 }, (_, i) => {
            const a = (i / 18) * Math.PI * 2 + (rand() - 0.5) * 0.3;
            return {
              m: add(sparkG, fill(ctx, sparkCols[i % sparkCols.length], 1, 1), 1), a, len: 80 + rand() * 120, w: 3 + rand() * 3, speed: 600 + rand() * 900,
            };
          });
          // --- はけの縁（潰れきる瞬間の横の硬い線）
          const edge = add(sparkG, fill(ctx, '#ffffff', PLATE_W, 4), 2);

          /** 火花 1 本（出どころ・向き・経過秒） */
          const placeSpark = (s, x0, y0, since, on) => {
            const k = Math.min(1, since / SPARK_LIFE);
            const r = s.speed * since * (1 - 0.4 * k);
            const len = s.len * (1 - k);
            const mid = r + len / 2;
            s.m.position.set(x0 + Math.cos(s.a) * mid, y0 + Math.sin(s.a) * mid, 0);
            s.m.rotation.z = s.a;
            s.m.scale.set(Math.max(0.001, len), s.w, 1);
            fade(s.m, on && k < 1 ? 1 : 0);
          };

          return {
            root,
            update: ({ localMs: t }) => {
              const u = hs(t);
              const x = cardX(t, W, side);
              fade(dim, DIM * seg(t, 0, 120) * (1 - seg(t, EXIT, GONE)));

              // --- 滑り込みの線（カードの後ろ、来た側へ伸びる）
              const la = seg(t, 20, 60) * (1 - seg(t, SLIDE_END, SLIDE_END + 40));
              trails.forEach((l) => {
                place(l.m, x + side * (CARD_W / 2 + l.len / 2), CY + l.dy, { sx: l.len, sy: l.w });
                fade(l.m, la);
              });

              // --- カードと板と鎖（一緒に縮む・潰れる）
              const squeeze = t < CLAMP ? 1 : SQUEEZE + (1 - SQUEEZE) * tween(t, CLAMP + STOP, CLAMP + STOP + 140, 'back_out');
              const squash = 1 - tween(t, EXIT, GONE, 'power2_in');
              place(bound, x, CY, { sx: squeeze, sy: Math.max(0.001, squeeze * squash) });
              fade(bound, t < GONE ? 1 : 0);
              fade(face, 1);
              fade(grey, GREY * tween(t, MARK_HIT, EXIT, 'ease_out'));
              fade(flash, t >= CLAMP && t < CLAMP + 34 ? 0.8 : 0);

              chains.forEach((c) => {
                const k = tween(t, CHAIN_IN + c.line.delay, CLAMP, 'power2_in');
                const { tail, head, d } = c.geo;
                const hx = tail[0] + (head[0] - tail[0]) * k;
                const hy = tail[1] + (head[1] - tail[1]) * k;
                const run = c.total * k;
                const rot = Math.atan2(-d[0], d[1]);
                c.links.forEach((m, i) => {
                  const back = i * step + lh / 2;
                  place(m, hx - d[0] * back, hy - d[1] * back, { rot });
                  fade(m, t >= CHAIN_IN + c.line.delay && back - lh / 2 < run ? 1 : 0);
                });
              });

              // --- 無効化の印: 上から大きく落ちて着地、少し潰れて戻る。はけは横に潰れる
              const fall = tween(t, MARK_IN, MARK_HIT, 'power3_in');
              const land = t < MARK_HIT ? 0 : Math.sin(seg(t, MARK_HIT, MARK_HIT + 140) * Math.PI);
              const ms = MARK_FROM + (1 - MARK_FROM) * fall;
              const msq = tween(t, EXIT, MARK_GONE, 'power2_in');
              place(markG, cx, CY - 520 * (1 - fall), {
                sx: Math.max(0.001, ms * (1 + 0.06 * land) * (1 - msq)), sy: ms * (1 - 0.08 * land),
              });
              fade(mark, t >= MARK_IN && t < MARK_GONE ? seg(t, MARK_IN, MARK_IN + 30) : 0);
              fade(markFlash, t >= MARK_HIT && t < MARK_HIT + 34 ? 0.9 : 0);

              // --- 火花（締まった点から。止めの間はその場で止まる）
              const sinceC = Math.max(0, (u - CLAMP) / 1000);
              clampSparks.forEach((s) => placeSpark(s, x + s.p[0] * squeeze, CY + s.p[1] * squeeze, sinceC, t >= CLAMP));
              const sinceM = Math.max(0, (t - MARK_HIT) / 1000);
              markSparks.forEach((s) => placeSpark(s, cx + Math.cos(s.a) * MARK * 0.46, CY + Math.sin(s.a) * MARK * 0.46, sinceM, t >= MARK_HIT));

              // --- 潰れきる瞬間の横の線
              place(edge, cx, CY, { sx: 1, sy: 1 });
              fade(edge, t >= GONE - 34 && t < GONE ? 0.9 : 0);
            },
          };
        },
      },
    ],
  };
}
